import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Heart, MessageCircle, Bookmark, Plus, ShoppingCart, Share2, Flag, EyeOff, Copy, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BottomNavigation from '@/components/BottomNavigation';
import ContentCreationFlow from '@/components/ContentCreation/ContentCreationFlow';
import CommentsModal from '@/components/CommentsModal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useUserSearch } from '@/hooks/useUserSearch';
import { Input } from '@/components/ui/input';

interface Post {
  id: string;
  user_id: string;
  content_type: string;
  media_urls: string[];
  caption: string | null;
  likes_count: number;
  comments_count: number;
  saves_count: number;
  created_at: string;
  profiles: {
    full_name: string;
    role: string;
    avatar_url: string;
    is_verified: boolean;
  } | null;
  restaurants: {
    id: string;
    name: string;
  } | null;
  menu_items: {
    id: string;
    name: string;
    price: number;
    image_url: string;
  } | null;
}

// Virtualized post component - only renders media when near viewport
const PostItem = React.memo(({
  post,
  isActive,
  isNearby,
  isLiked,
  isSaved,
  isFollowing,
  isOwnPost,
  onLike,
  onSave,
  onComment,
  onShare,
  onFollow,
  onAddToCart,
  onUserClick,
  onDoubleTap,
}: {
  post: Post;
  isActive: boolean;
  isNearby: boolean;
  isLiked: boolean;
  isSaved: boolean;
  isFollowing: boolean;
  isOwnPost: boolean;
  onLike: () => void;
  onSave: () => void;
  onComment: () => void;
  onShare: () => void;
  onFollow: () => void;
  onAddToCart: () => void;
  onUserClick: () => void;
  onDoubleTap: (e: React.TouchEvent | React.MouseEvent) => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive) {
      video.play().catch(() => {});
    } else {
      video.pause();
      // Reset to start when scrolled away
      if (!isNearby) {
        video.currentTime = 0;
      }
    }
  }, [isActive, isNearby]);

  return (
    <div className="h-screen w-full relative flex-shrink-0 snap-start snap-always">
      {/* Media - only render if nearby */}
      <div
        className="absolute inset-0"
        onDoubleClick={onDoubleTap}
        onTouchEnd={onDoubleTap}
      >
        {isNearby ? (
          post.content_type === 'video' ? (
            <video
              ref={videoRef}
              src={post.media_urls[0]}
              className="w-full h-full object-cover"
              loop
              muted
              playsInline
              preload={isActive ? 'auto' : 'metadata'}
            />
          ) : (
            <img
              src={post.media_urls[0]}
              alt="Post content"
              className="w-full h-full object-cover"
              loading={isActive ? 'eager' : 'lazy'}
            />
          )
        ) : (
          <div className="w-full h-full bg-black" />
        )}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none">
        {/* Right Side Actions */}
        <div className="absolute right-4 bottom-20 flex flex-col gap-6 pointer-events-auto">
          <button onClick={onLike} className="p-3 rounded-full bg-black/30 backdrop-blur-sm active:scale-110 transition-transform">
            <Heart className={`h-6 w-6 transition-colors ${isLiked ? 'text-red-500 fill-red-500' : 'text-white'}`} />
            <span className="text-white text-xs mt-1 block">{post.likes_count}</span>
          </button>

          <button onClick={onComment} className="p-3 rounded-full bg-black/30 backdrop-blur-sm active:scale-110 transition-transform">
            <MessageCircle className="h-6 w-6 text-white" />
            <span className="text-white text-xs mt-1 block">{post.comments_count}</span>
          </button>

          <button onClick={onSave} className="p-3 rounded-full bg-black/30 backdrop-blur-sm active:scale-110 transition-transform">
            <Bookmark className={`h-6 w-6 transition-colors ${isSaved ? 'text-yellow-500 fill-yellow-500' : 'text-white'}`} />
          </button>

          <button onClick={onShare} className="p-3 rounded-full bg-black/30 backdrop-blur-sm active:scale-110 transition-transform">
            <Share2 className="h-6 w-6 text-white" />
          </button>
        </div>

        {/* Bottom Content */}
        <div className="absolute bottom-20 left-4 right-20 pointer-events-auto">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center overflow-hidden">
              {post.profiles?.avatar_url ? (
                <img src={post.profiles.avatar_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-sm font-bold">
                  {(post.profiles?.full_name || 'User')?.charAt(0)?.toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-1">
              <button onClick={onUserClick} className="text-white font-semibold hover:underline">
                {post.profiles?.full_name || `User ${post.user_id.slice(0, 8)}`}
              </button>
              {post.profiles?.is_verified && (
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
              {!isOwnPost && !isFollowing && (
                <button onClick={onFollow} className="ml-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                  <Plus className="h-4 w-4 text-white" />
                </button>
              )}
            </div>
          </div>

          {post.caption && (
            <p className="text-white mb-2 text-sm leading-relaxed">{post.caption}</p>
          )}

          {post.restaurants && (
            <div className="text-white/80 text-sm mb-3">
              <span>📍 {post.restaurants.name}</span>
              {post.menu_items && <span className="ml-2">• {post.menu_items.name}</span>}
            </div>
          )}

          {post.menu_items && post.restaurants && (
            <div className="mb-2">
              <Button
                onClick={onAddToCart}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2"
              >
                <ShoppingCart className="h-4 w-4" />
                Add to Cart - GMD {post.menu_items.price}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

PostItem.displayName = 'PostItem';

const ExplorePage = () => {
  const [showContentCreation, setShowContentCreation] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [savedPosts, setSavedPosts] = useState<Set<string>>(new Set());
  const [followedUsers, setFollowedUsers] = useState<Set<string>>(new Set());
  const [sharePost, setSharePost] = useState<Post | null>(null);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [commentsModal, setCommentsModal] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTapRef = useRef(0);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [searchParams] = useSearchParams();
  const { users: searchResults, loading: searchLoading } = useUserSearch(searchQuery);

  // Set theme-color to black for immersive status bar (like TikTok)
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', '#000000');
    }
    return () => {
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', '#ffffff');
      }
    };
  }, []);

  useEffect(() => {
    fetchPosts();
    if (user) fetchUserInteractions();

    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    return () => {
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, [user]);

  // Scroll to specific post from URL
  useEffect(() => {
    const postId = searchParams.get('postId');
    if (postId && posts.length > 0) {
      const postIndex = posts.findIndex(p => p.id === postId);
      if (postIndex !== -1 && containerRef.current) {
        setCurrentPostIndex(postIndex);
        containerRef.current.scrollTo({ top: postIndex * window.innerHeight, behavior: 'instant' as ScrollBehavior });
      }
    }
  }, [posts, searchParams]);

  // Track current post via IntersectionObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container || posts.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const idx = Number(entry.target.getAttribute('data-index'));
            if (!isNaN(idx)) setCurrentPostIndex(idx);
          }
        }
      },
      { root: container, threshold: 0.5 }
    );

    const items = container.querySelectorAll('[data-index]');
    items.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [posts]);

  // Listen for comment count refreshes
  useEffect(() => {
    const handleRefresh = () => fetchPosts();
    window.addEventListener('refreshPosts', handleRefresh);
    return () => window.removeEventListener('refreshPosts', handleRefresh);
  }, []);

  const fetchPosts = async () => {
    try {
      // Single query - use counts already stored on posts table
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          profiles!posts_user_id_fkey(full_name, role, avatar_url, is_verified),
          restaurants(id, name),
          menu_items(id, name, price, image_url)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts((data || []) as Post[]);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserInteractions = async () => {
    if (!user) return;
    try {
      const [likesRes, savesRes, followsRes] = await Promise.all([
        supabase.from('post_likes').select('post_id').eq('user_id', user.id),
        supabase.from('post_saves').select('post_id').eq('user_id', user.id),
        supabase.from('user_follows').select('following_id').eq('follower_id', user.id),
      ]);

      if (likesRes.data) setLikedPosts(new Set(likesRes.data.map(l => l.post_id)));
      if (savesRes.data) setSavedPosts(new Set(savesRes.data.map(s => s.post_id)));
      if (followsRes.data) setFollowedUsers(new Set(followsRes.data.map(f => f.following_id)));
    } catch (error) {
      console.error('Error fetching user interactions:', error);
    }
  };

  // Optimistic like
  const handleLike = useCallback(async (postId: string) => {
    if (!user) {
      toast({ title: "Please log in", description: "You need to be logged in to like posts", variant: "destructive" });
      return;
    }

    const wasLiked = likedPosts.has(postId);

    // Optimistic update
    setLikedPosts(prev => {
      const next = new Set(prev);
      wasLiked ? next.delete(postId) : next.add(postId);
      return next;
    });
    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, likes_count: p.likes_count + (wasLiked ? -1 : 1) } : p
    ));

    if (navigator.vibrate && !wasLiked) navigator.vibrate(50);

    try {
      if (wasLiked) {
        await supabase.from('post_likes').delete().eq('post_id', postId).eq('user_id', user.id);
      } else {
        await supabase.from('post_likes').insert({ post_id: postId, user_id: user.id });
      }
      // Fire-and-forget count update
      supabase.rpc('update_post_likes_count', { post_id: postId }).then(() => {});
    } catch {
      // Revert on error
      setLikedPosts(prev => {
        const next = new Set(prev);
        wasLiked ? next.add(postId) : next.delete(postId);
        return next;
      });
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, likes_count: p.likes_count + (wasLiked ? 1 : -1) } : p
      ));
    }
  }, [user, likedPosts, toast]);

  // Optimistic save
  const handleSave = useCallback(async (postId: string) => {
    if (!user) {
      toast({ title: "Please log in", description: "You need to be logged in to save posts", variant: "destructive" });
      return;
    }

    const wasSaved = savedPosts.has(postId);

    // Optimistic update
    setSavedPosts(prev => {
      const next = new Set(prev);
      wasSaved ? next.delete(postId) : next.add(postId);
      return next;
    });
    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, saves_count: p.saves_count + (wasSaved ? -1 : 1) } : p
    ));

    toast({ title: wasSaved ? "Post removed" : "Post saved", description: wasSaved ? "Removed from saved" : "Saved to collection" });

    try {
      if (wasSaved) {
        await supabase.from('post_saves').delete().eq('post_id', postId).eq('user_id', user.id);
      } else {
        await supabase.from('post_saves').insert({ post_id: postId, user_id: user.id });
      }
    } catch {
      setSavedPosts(prev => {
        const next = new Set(prev);
        wasSaved ? next.add(postId) : next.delete(postId);
        return next;
      });
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, saves_count: p.saves_count + (wasSaved ? 1 : -1) } : p
      ));
    }
  }, [user, savedPosts, toast]);

  const handleFollow = useCallback(async (userId: string) => {
    if (!user) {
      toast({ title: "Please log in", description: "You need to be logged in to follow users", variant: "destructive" });
      return;
    }

    const wasFollowing = followedUsers.has(userId);
    setFollowedUsers(prev => {
      const next = new Set(prev);
      wasFollowing ? next.delete(userId) : next.add(userId);
      return next;
    });

    try {
      if (wasFollowing) {
        await supabase.from('user_follows').delete().eq('follower_id', user.id).eq('following_id', userId);
      } else {
        await supabase.from('user_follows').insert({ follower_id: user.id, following_id: userId });
      }
    } catch {
      setFollowedUsers(prev => {
        const next = new Set(prev);
        wasFollowing ? next.add(userId) : next.delete(userId);
        return next;
      });
    }
  }, [user, followedUsers, toast]);

  const handleAddToCart = useCallback(async (post: Post) => {
    if (!user) {
      toast({ title: "Please log in", description: "You need to be logged in to add items to cart", variant: "destructive" });
      return;
    }
    if (!post.menu_items || !post.restaurants) {
      toast({ title: "Item unavailable", description: "This item is not available for ordering", variant: "destructive" });
      return;
    }

    try {
      const { data: menuItemData } = await supabase.from('menu_items').select('image_url, is_customizable').eq('id', post.menu_items.id).single();

      if (menuItemData?.is_customizable) {
        navigate(`/restaurant/${post.restaurants.id}?item=${post.menu_items.id}`);
        return;
      }

      await addItem({
        menu_item_id: post.menu_items.id,
        name: post.menu_items.name,
        price: post.menu_items.price,
        restaurant_id: post.restaurants.id,
        restaurant_name: post.restaurants.name,
        image_url: menuItemData?.image_url || null,
        referral_post_id: post.id,
        cart_type: 'restaurant',
      });

      
      navigate(`/restaurant/${post.restaurants.id}`);
    } catch {
      toast({ title: "Error", description: "Failed to add item to cart", variant: "destructive" });
    }
  }, [user, toast, navigate, addItem]);

  const handleDoubleTap = useCallback((postId: string, e: React.TouchEvent | React.MouseEvent) => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      e.preventDefault();
      handleLike(postId);
      if (navigator.vibrate) navigator.vibrate([50, 30, 50]);
    }
    lastTapRef.current = now;
  }, [handleLike]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.origin + '/explore');
    toast({ title: "Link copied", description: "Post link copied to clipboard" });
    setSharePost(null);
  }, [toast]);

  const shareToWhatsApp = useCallback(() => {
    if (sharePost) {
      const text = `Check out this post: ${sharePost.caption || 'Amazing content!'} ${window.location.origin}/explore`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  }, [sharePost]);

  const shareToFacebook = useCallback(() => {
    if (sharePost) {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/explore')}`, '_blank');
    }
  }, [sharePost]);

  const handleReport = useCallback(async (postId: string) => {
    if (!user) {
      toast({ title: "Please log in", description: "You need to be logged in to report posts", variant: "destructive" });
      return;
    }
    try {
      await supabase.from('post_reports').insert({ post_id: postId, user_id: user.id, reason: 'Inappropriate content' });
      toast({ title: "Post reported", description: "Thank you for your report. We'll review it soon." });
    } catch {
      toast({ title: "Error", description: "Failed to report post. You may have already reported this post.", variant: "destructive" });
    }
    setSharePost(null);
  }, [user, toast]);

  const handleNotInterested = useCallback((postId: string) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    toast({ title: "Post hidden", description: "You won't see posts like this anymore" });
    setSharePost(null);
  }, [toast]);

  if (loading) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-black relative overflow-hidden">
      {/* Search Bar */}
      <div className="absolute top-4 left-4 z-20">
        <div className="relative">
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSearchResults(!!e.target.value.trim());
            }}
            className="w-60 h-12 pl-12 rounded-lg bg-white/20 backdrop-blur-sm border border-white/30 placeholder:text-white/70 text-white"
          />
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/70" />

          {showSearchResults && searchQuery.trim() && (
            <div className="absolute top-14 left-0 w-full max-h-80 overflow-y-auto bg-white/90 backdrop-blur-sm rounded-lg border border-white/30 shadow-lg">
              {searchLoading ? (
                <div className="p-4 text-center text-gray-600">Searching...</div>
              ) : searchResults.length > 0 ? (
                searchResults.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => { navigate(`/profile/${u.id}`); setSearchQuery(''); setShowSearchResults(false); }}
                    className="w-full p-3 hover:bg-white/50 transition-colors border-b border-white/20 last:border-b-0 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{u.full_name}</span>
                        {u.is_verified && (
                          <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-600">No users found</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Post Button */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          size="sm"
          className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30"
          onClick={() => setShowContentCreation(true)}
        >
          <Plus className="h-6 w-6 text-white" />
        </Button>
      </div>

      {posts.length === 0 ? (
        <div className="h-full w-full flex items-center justify-center">
          <div className="text-center text-white px-8">
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">No content yet</h2>
            <p className="text-white/80 text-sm">There's no content, be the first to make a post</p>
          </div>
        </div>
      ) : (
        <div
          ref={containerRef}
          className="h-full w-full overflow-y-scroll"
          style={{
            scrollSnapType: 'y mandatory',
            scrollSnapStop: 'always',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {posts.map((post, index) => (
            <div key={post.id} data-index={index} style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}>
              <PostItem
                post={post}
                isActive={index === currentPostIndex}
                isNearby={Math.abs(index - currentPostIndex) <= 1}
                isLiked={likedPosts.has(post.id)}
                isSaved={savedPosts.has(post.id)}
                isFollowing={followedUsers.has(post.user_id)}
                isOwnPost={post.user_id === user?.id}
                onLike={() => handleLike(post.id)}
                onSave={() => handleSave(post.id)}
                onComment={() => setCommentsModal(post.id)}
                onShare={() => setSharePost(post)}
                onFollow={() => handleFollow(post.user_id)}
                onAddToCart={() => handleAddToCart(post)}
                onUserClick={() => navigate(`/profile/${post.user_id}`)}
                onDoubleTap={(e) => handleDoubleTap(post.id, e)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Comments Modal */}
      {commentsModal && (
        <CommentsModal
          isOpen={!!commentsModal}
          onClose={() => setCommentsModal(null)}
          postId={commentsModal}
        />
      )}

      <BottomNavigation />

      {/* Share Modal */}
      {sharePost && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end" onClick={() => setSharePost(null)}>
          <div className="bg-white rounded-t-3xl w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-center">Share Post</h3>

            <div className="grid grid-cols-4 gap-4 py-4">
              <button onClick={() => handleCopyLink()} className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-100">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Copy className="h-6 w-6 text-white" />
                </div>
                <span className="text-xs text-gray-600">Instagram</span>
              </button>
              <button onClick={shareToWhatsApp} className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-100">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <Share2 className="h-6 w-6 text-white" />
                </div>
                <span className="text-xs text-gray-600">WhatsApp</span>
              </button>
              <button onClick={shareToFacebook} className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-100">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <Share2 className="h-6 w-6 text-white" />
                </div>
                <span className="text-xs text-gray-600">Facebook</span>
              </button>
              <button onClick={handleCopyLink} className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-100">
                <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center">
                  <Copy className="h-6 w-6 text-white" />
                </div>
                <span className="text-xs text-gray-600">Copy Link</span>
              </button>
            </div>

            <div className="border-t pt-4 space-y-3">
              <button onClick={() => handleReport(sharePost.id)} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100">
                <Flag className="h-5 w-5 text-gray-600" />
                <span className="text-gray-800">Report Post</span>
              </button>
              <button onClick={() => handleNotInterested(sharePost.id)} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100">
                <EyeOff className="h-5 w-5 text-gray-600" />
                <span className="text-gray-800">Not Interested</span>
              </button>
            </div>

            <button onClick={() => setSharePost(null)} className="w-full p-3 text-center text-gray-500 font-medium">
              Cancel
            </button>
          </div>
        </div>
      )}

      {showContentCreation && (
        <ContentCreationFlow
          onClose={() => {
            setShowContentCreation(false);
            fetchPosts();
          }}
        />
      )}
    </div>
  );
};

export default ExplorePage;
